<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    getUser,
    getUserSessions,
    listRooms,
    listSchools,
    fetchStudentProfileImage,
    fetchDashboardLive,
    fetchLogs,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import { scopes } from "../lib/session.svelte";
  import type {
    AdminUser,
    AdminRoom,
    School,
    DashboardStudent,
    AdminUserSession,
    AuditEntry,
  } from "@diy-avatar/shared/admin";

  // svelte-spa-router passes route params via the `params` prop.
  export let params: { id?: string } = {};
  $: userId = params.id ? parseInt(params.id, 10) : NaN;

  let user: AdminUser | null = null;
  let rooms: AdminRoom[] = [];
  let schools: School[] = [];
  let profileImage: string | null = null;
  let live: DashboardStudent | null = null;
  let liveUnavailable = false;

  let lastLogin: number | null = null;
  let sessions: AdminUserSession[] = [];
  let sessionsUnavailable = false;

  let activity: AuditEntry[] = [];
  let activityUnavailable = false;

  let loading = false;
  let error: string | null = null;
  let notFound = false;

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function schoolName(id: number | null | undefined): string {
    if (id == null) return "—";
    return schools.find((s) => s.id === id)?.name ?? `school ${id}`;
  }

  function fmtDate(ms: number | null | undefined): string {
    if (ms == null) return "—";
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return String(ms);
    }
  }

  // Rooms the user is assigned to (by email membership).
  function userRooms(): AdminRoom[] {
    if (!user) return [];
    return rooms.filter((r) => r.students.includes(user!.email));
  }

  function formatDuration(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${s % 60}s`;
  }

  function titleFor(u: AdminUser): string {
    if (u.full_name && u.display_name) return `${u.full_name} (${u.display_name})`;
    return u.full_name ?? u.display_name ?? u.email;
  }

  function initials(u: AdminUser): string {
    return (u.full_name ?? u.display_name ?? u.email).slice(0, 1).toUpperCase();
  }

  async function load(id: number) {
    if (!Number.isFinite(id)) return;
    loading = true;
    error = null;
    notFound = false;
    try {
      const res = await getUser(id);
      user = res.user;
    } catch (err) {
      if (handleAuth(err)) return;
      if (err instanceof ApiError && err.status === 404) {
        notFound = true;
        user = null;
      } else if (
        err instanceof ApiError &&
        (err.message === "not_found" || err.message === "not-found")
      ) {
        notFound = true;
        user = null;
      } else {
        error = err instanceof Error ? err.message : "Could not load user.";
      }
      loading = false;
      return;
    }

    // Best-effort context loads — each degrades independently, never throws.
    const [rm, sc] = await Promise.all([
      listRooms().catch(() => ({ rooms: [] as AdminRoom[] })),
      listSchools().catch(() => ({ schools: [] as School[] })),
    ]);
    rooms = rm.rooms;
    schools = sc.schools;
    loading = false;

    // Avatar.
    if (user && user.has_profile_image) {
      try {
        profileImage = await fetchStudentProfileImage(String(user.id));
      } catch {
        profileImage = null;
      }
    } else {
      profileImage = null;
    }

    // Live connection.
    try {
      const snap = await fetchDashboardLive();
      live = snap.connected_students.find((s) => s.id === String(id)) ?? null;
      liveUnavailable = false;
    } catch {
      live = null;
      liveUnavailable = true;
    }

    // Sessions.
    try {
      const ss = await getUserSessions(id);
      lastLogin = ss.last_login;
      sessions = ss.sessions;
      sessionsUnavailable = false;
    } catch {
      lastLogin = null;
      sessions = [];
      sessionsUnavailable = true;
    }

    // Recent activity.
    try {
      const logs = await fetchLogs({ user: String(id), limit: 10 });
      activity = logs.entries.slice(0, 10);
      activityUnavailable = false;
    } catch {
      activity = [];
      activityUnavailable = true;
    }
  }

  $: if (Number.isFinite(userId)) void load(userId);
  onMount(() => {});
</script>

<section>
  <Breadcrumb
    items={[
      { label: "User", href: "/users" },
      { label: user ? titleFor(user) : "…" },
    ]}
  />

  <div class="head">
    <a href="/users" use:link data-testid="back-users">← User</a>
    {#if user}
      <a
        class="edit"
        href="/users/{user.id}/edit"
        use:link
        data-testid="user-edit-link"
      >
        Edit
      </a>
    {/if}
  </div>

  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  {#if user}
    <div class="banner">
      {#if profileImage}
        <img src={profileImage} alt="avatar" class="avatar" data-testid="user-avatar" />
      {:else}
        <div class="avatar placeholder" aria-hidden="true">{initials(user)}</div>
      {/if}
      <div class="banner-text">
        <h1 data-testid="user-detail-email">{titleFor(user)}</h1>
        <p class="sub">{user.email}</p>
        <div class="badges">
          {#if user.disabled}
            <span class="badge fail">disabled</span>
          {:else}
            <span class="badge ok">active</span>
          {/if}
          {#if user.email_verified}
            <span class="badge ok">verified</span>
          {:else}
            <span class="badge warn">unverified</span>
          {/if}
        </div>
      </div>
    </div>

    <div class="layout" data-testid="user-detail">
      <div class="card" data-testid="user-profile">
        <h2>Profile</h2>
        <dl>
          <dt>Email</dt>
          <dd data-testid="detail-email">{user.email}</dd>
          <dt>Display name</dt>
          <dd>{user.display_name ?? "—"}</dd>
          <dt>Full name</dt>
          <dd>{user.full_name ?? "—"}</dd>
          <dt>Language</dt>
          <dd>{user.language}</dd>
          <dt>Log level</dt>
          <dd>{user.loglevel}</dd>
          <dt>Disabled</dt>
          <dd>{user.disabled ? "Yes" : "No"}</dd>
          <dt>Email verified</dt>
          <dd>{user.email_verified ? "Yes" : "No"}</dd>
          <dt>Created</dt>
          <dd>{fmtDate(user.created_at)}</dd>
          <dt>Max sessions</dt>
          <dd>{user.max_sessions ?? 1}</dd>
        </dl>
      </div>

      <div class="card" data-testid="user-roles">
        <h2>Roles &amp; school</h2>
        <ul class="roles">
          {#each user.roles as r (r.id)}
            <li>
              <span class="role-badge">{r.role}</span>
              {#if r.school_id != null}
                <a href="/schools/{r.school_id}" use:link>{schoolName(r.school_id)}</a>
              {:else if r.authority_id != null}
                {#if $scopes.isAdmin}
                  <a href="/authorities/{r.authority_id}" use:link
                    >{r.authority_name ?? `authority ${r.authority_id}`}</a
                  >
                {:else}
                  <span class="muted">{r.authority_name ?? `authority ${r.authority_id}`}</span>
                {/if}
              {:else}
                <span class="muted">global</span>
              {/if}
            </li>
          {/each}
          {#if user.roles.length === 0}
            <li class="muted">no roles</li>
          {/if}
        </ul>
      </div>

      <div class="card" data-testid="user-rooms">
        <h2>Room assignments</h2>
        <ul class="rooms-list">
          {#each userRooms() as r (r.id)}
            <li>
              <a href="/rooms/{r.id}" use:link>{r.name}</a>
              <span class="muted">· {schoolName(r.school_id)}</span>
            </li>
          {/each}
          {#if userRooms().length === 0}
            <li class="muted">not assigned to any room</li>
          {/if}
        </ul>
      </div>

      <div class="card" data-testid="user-live">
        <h2>Live connection</h2>
        {#if liveUnavailable}
          <p class="muted">live connection data unavailable</p>
        {:else if live}
          <dl>
            <dt>Status</dt>
            <dd><span class="dot online"></span> connected</dd>
            <dt>Room</dt>
            <dd>
              {#if live.room_id != null}
                <a href="/rooms/{live.room_id}" use:link
                  >{live.room_name ?? `room ${live.room_id}`}</a
                >
              {:else}
                <span class="muted">—</span>
              {/if}
            </dd>
            <dt>Device</dt>
            <dd>
              {#if live.device_label}
                <a href="/devices/{encodeURIComponent(live.device_label)}" use:link
                  >{live.device_label}</a
                >
              {:else}
                <span class="muted">—</span>
              {/if}
            </dd>
            <dt>Duration</dt>
            <dd>
              {live.connected_since != null
                ? formatDuration((Date.now() - live.connected_since) / 1000)
                : "—"}
            </dd>
          </dl>
        {:else}
          <p><span class="dot"></span> not connected</p>
        {/if}
      </div>

      <div class="card" data-testid="user-sessions">
        <h2>Sessions</h2>
        <p class="lastlogin">
          Last login: <span data-testid="last-login">{fmtDate(lastLogin) === "—"
            ? "never"
            : fmtDate(lastLogin)}</span>
        </p>
        {#if sessionsUnavailable}
          <p class="muted">session data unavailable</p>
        {:else if sessions.length === 0}
          <p class="muted">no active sessions</p>
        {:else}
          <table class="sessions">
            <thead>
              <tr><th>device</th><th>browser</th><th>ip</th><th>since</th></tr>
            </thead>
            <tbody>
              {#each sessions as s, i (i)}
                <tr>
                  <td>{s.device ?? "—"}</td>
                  <td>{s.browser ?? "—"}</td>
                  <td>{s.ip ?? "—"}</td>
                  <td>{fmtDate(s.created_at)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </div>

      <div class="card" data-testid="user-activity">
        <div class="card-head">
          <h2>Recent activity</h2>
          <a href="/logs" use:link class="viewall" data-testid="activity-view-all"
            >view all</a
          >
        </div>
        {#if activityUnavailable}
          <p class="muted">activity unavailable</p>
        {:else if activity.length === 0}
          <p class="muted">no recent activity</p>
        {:else}
          <ul class="activity">
            {#each activity as a (a.id)}
              <li>
                <span class="lvl {a.level ?? 'info'}">{a.level ?? "info"}</span>
                <span class="event">{a.event}</span>
                <span class="muted ts">{fmtDate(a.ts)}</span>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    </div>
  {:else if !loading}
    <p class="empty" data-testid="not-found">User not found.</p>
  {/if}
</section>

<style>
  h1 { margin: 0; }
  h2 { margin: 0 0 0.5rem; font-size: 1rem; color: #34d399; }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  .head > a {
    color: #34d399;
    text-decoration: none;
  }
  .head > a:hover { text-decoration: underline; }
  .edit {
    padding: 0.35rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6 !important;
    border: 1px solid #374151;
    border-radius: 6px;
    text-decoration: none;
    font-size: 0.85rem;
  }
  .banner {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .avatar {
    width: 72px;
    height: 72px;
    border-radius: 8px;
    object-fit: cover;
    background: #0f172a;
    flex: none;
  }
  .avatar.placeholder {
    display: grid;
    place-items: center;
    color: #6ee7b7;
    font-size: 1.8rem;
    font-weight: 700;
    border: 1px solid #1f2937;
  }
  .banner-text { min-width: 0; }
  .sub { margin: 0.2rem 0 0.4rem; color: #9ca3af; }
  .badges { display: flex; gap: 0.4rem; flex-wrap: wrap; }
  .badge {
    padding: 0.1rem 0.45rem;
    border-radius: 8px;
    font-size: 0.7rem;
    background: #1f2937;
  }
  .layout {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  .card {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .card-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .viewall { font-size: 0.8rem; }
  dl {
    margin: 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.2rem 0.75rem;
  }
  dt { color: #9ca3af; }
  dd { margin: 0; color: #f3f4f6; }
  a { color: #34d399; text-decoration: none; }
  a:hover { text-decoration: underline; }
  .roles, .rooms-list, .activity {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.4rem;
  }
  .roles li, .rooms-list li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
  }
  .role-badge {
    background: #1f2937;
    color: #d1d5db;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.75rem;
  }
  .lastlogin { margin: 0 0 0.5rem; font-size: 0.85rem; color: #9ca3af; }
  table.sessions { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  table.sessions th, table.sessions td {
    padding: 0.3rem 0.4rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
  }
  table.sessions th { color: #9ca3af; }
  .activity li {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.85rem;
  }
  .activity .event { color: #f3f4f6; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
  .activity .ts { font-size: 0.75rem; white-space: nowrap; }
  .lvl {
    padding: 0.05rem 0.35rem;
    border-radius: 6px;
    font-size: 0.65rem;
    background: #1f2937;
    color: #9ca3af;
    flex: none;
  }
  .lvl.warn { color: #fbbf24; }
  .lvl.error { color: #fca5a5; }
  .muted { color: #6b7280; }
  .ok { color: #6ee7b7; }
  .warn { color: #fbbf24; }
  .fail { color: #fca5a5; }
  .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #6b7280;
    margin-right: 0.3rem;
  }
  .dot.online { background: #10b981; }
  .empty { color: #6b7280; }
  .error { color: #fca5a5; }
</style>
